// Ersatz für confirm()/prompt()/alert().
//
// Native Dialoge blockieren den Hauptthread, sehen auf jedem System anders
// aus und werden in installierten PWAs teils unterdrückt – eine „Wirklich
// löschen?"-Rückfrage, die nie erscheint, ist ein handfestes Datenrisiko.
import { el } from './dom.js';
import { openSheet } from './sheet.js';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function confirmDialog({
  title,
  message,
  confirmLabel = 'Ja',
  cancelLabel = 'Abbrechen',
  danger = false,
}: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
      sheet.close();
    };

    const sheet = openSheet(message ? el('p', { class: 'muted' }, message) : null, {
      title,
      onClose: () => finish(false),
    });

    sheet.addActions(
      el(
        'button',
        {
          class: danger ? 'btn btn-danger btn-block' : 'btn btn-primary btn-block',
          type: 'button',
          onclick: () => finish(true),
        },
        confirmLabel,
      ),
      el(
        'button',
        { class: 'btn btn-secondary btn-block', type: 'button', onclick: () => finish(false) },
        cancelLabel,
      ),
    );
  });
}

export interface PromptOptions {
  title: string;
  label?: string;
  value?: string;
  placeholder?: string;
  confirmLabel?: string;
  multiline?: boolean;
}

export function promptDialog({
  title,
  label,
  value = '',
  placeholder = '',
  confirmLabel = 'Übernehmen',
  multiline = false,
}: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const field = multiline
      ? el('textarea', { class: 'textarea', placeholder }, value)
      : el('input', { class: 'input', type: 'text', value, placeholder });

    let settled = false;
    const finish = (result: string | null) => {
      if (settled) return;
      settled = true;
      resolve(result);
      sheet.close();
    };

    const body = el('div', { class: 'field' }, [label ? el('label', {}, label) : null, field]);

    const sheet = openSheet(body, { title, onClose: () => finish(null) });

    if (!multiline) {
      field.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') finish(field.value.trim() || null);
      });
    }

    sheet.addActions(
      el(
        'button',
        {
          class: 'btn btn-primary btn-block',
          type: 'button',
          onclick: () => finish(field.value.trim() || null),
        },
        confirmLabel,
      ),
    );

    requestAnimationFrame(() => field.focus());
  });
}

/** Reine Information mit einer Schaltfläche. */
export function alertDialog(title: string, message: string): Promise<void> {
  return new Promise((resolve) => {
    const sheet = openSheet(el('p', { class: 'muted' }, message), {
      title,
      onClose: () => resolve(),
    });
    sheet.addActions(
      el(
        'button',
        { class: 'btn btn-primary btn-block', type: 'button', onclick: () => sheet.close() },
        'Alles klar',
      ),
    );
  });
}
